import { useState } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  PasswordInput,
  Button,
  Group,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/api/auth';

export function UpdatePasswordPage() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required.';
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required.';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password does not meet the requirements.';
    } else if (newPassword.length > 128) {
      newErrors.newPassword = 'Password does not meet the requirements.';
    }

    if (!confirmNewPassword) {
      newErrors.confirmNewPassword = 'Please confirm your new password.';
    } else if (newPassword && confirmNewPassword !== newPassword) {
      newErrors.confirmNewPassword = 'Passwords do not match.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      await authService.changePassword({
        currentPassword,
        newPassword,
        confirmNewPassword,
      });

      notifications.show({
        title: 'Success',
        message: 'Password updated successfully.',
        color: 'green',
      });

      // Reset form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setErrors({});
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update password.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  // Clear field-level errors on input change
  const handleCurrentPasswordChange = (value: string) => {
    setCurrentPassword(value);
    if (errors.currentPassword) {
      setErrors(prev => { const next = { ...prev }; delete next.currentPassword; return next; });
    }
  };

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    if (errors.newPassword) {
      setErrors(prev => { const next = { ...prev }; delete next.newPassword; return next; });
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmNewPassword(value);
    if (errors.confirmNewPassword) {
      setErrors(prev => { const next = { ...prev }; delete next.confirmNewPassword; return next; });
    }
  };

  return (
    <Container size="sm" py="md">
      <Title order={2} mb="lg">
        Update Password
      </Title>

      <Paper withBorder p="xl" radius="md">
        <Stack gap="md">
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
          >
            <Text size="sm">
              Password must be between 8 and 128 characters.
            </Text>
          </Alert>

          <PasswordInput
            label="Current Password"
            placeholder="Enter your current password"
            value={currentPassword}
            onChange={e => handleCurrentPasswordChange(e.currentTarget.value)}
            error={errors.currentPassword}
            required
          />

          <PasswordInput
            label="New Password"
            placeholder="Enter your new password"
            value={newPassword}
            onChange={e => handleNewPasswordChange(e.currentTarget.value)}
            error={errors.newPassword}
            required
          />

          <PasswordInput
            label="Confirm New Password"
            placeholder="Re-enter your new password"
            value={confirmNewPassword}
            onChange={e => handleConfirmPasswordChange(e.currentTarget.value)}
            error={errors.confirmNewPassword}
            required
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={loading}>
              Change My Password
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Container>
  );
}
